// @hssna/ui-kit/common/Button — 通用按钮组件
// 规格: SPEC §6 / ADR-0012 (Tailwind) / OLD-01 (适老化)
//
// 支持:
//   - 5 种变体 (primary/secondary/danger/warning/ghost)
//   - 4 种尺寸 (sm/md/lg/elderly)
//   - loading 状态 (内置 spinner, 自动 disabled)
//   - 适老化大按钮 (elderly 尺寸: 大字号 + 高对比度 + 48x48 最小点击区)
//
// 主题: 通过 .theme-elderly CSS 变量自动适配适老化样式

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { LoadingSpinner } from './LoadingSpinner.js';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'warning' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'elderly';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** 按钮变体 */
  readonly variant?: ButtonVariant;
  /** 按钮尺寸 (elderly 为适老化大按钮) */
  readonly size?: ButtonSize;
  /** 加载中状态 (显示 spinner 并禁用点击) */
  readonly loading?: boolean;
  /** 左侧图标 */
  readonly leftIcon?: ReactNode;
  /** 右侧图标 */
  readonly rightIcon?: ReactNode;
  /** 子节点 */
  readonly children?: ReactNode;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-primary text-white hover:bg-blue-700 focus:ring-brand-primary border border-transparent',
  secondary:
    'bg-white text-gray-800 hover:bg-gray-50 focus:ring-brand-primary border border-gray-300',
  danger:
    'bg-alert-red text-white hover:bg-red-700 focus:ring-alert-red border border-transparent',
  warning:
    'bg-alert-yellow text-white hover:bg-yellow-700 focus:ring-alert-yellow border border-transparent',
  ghost:
    'bg-transparent text-brand-primary hover:bg-blue-50 focus:ring-brand-primary border border-transparent',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm rounded-md min-h-[36px]',
  md: 'px-4 py-2 text-base rounded-md min-h-[40px]',
  lg: 'px-6 py-3 text-lg rounded-lg min-h-[44px]',
  elderly:
    'px-8 py-4 text-elderly rounded-lg min-h-[48px] font-semibold border-2',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  children,
  className = '',
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const classes = [
    'inline-flex items-center justify-center gap-2 font-medium transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[size],
    className,
  ].join(' ');

  return (
    <button type={type} className={classes} disabled={isDisabled} {...rest}>
      {loading && <LoadingSpinner size={size === 'sm' ? 'sm' : 'md'} />}
      {!loading && leftIcon}
      {children && <span>{children}</span>}
      {!loading && rightIcon}
    </button>
  );
}
